import { cva } from "class-variance-authority";
import type { MouseEventHandler } from "react";
import { Button } from "../../elements/button/button";
import { Image, type ImageProps } from "../../elements/image/image";
import { Text } from "../../elements/text/text";
import { cn } from "../../lib/utils";

export type CardGenericProps = {
  variant:
    | "primary-large"
    | "primary-medium"
    | "primary-small"
    | "secondary"
    | "secondary-image-full";
  title: string;
  overlayImage: {
    url: string;
    alt: string;
  };
  description?: string;
  button: {
    text: string;
    href: string;
    onClick?: MouseEventHandler<HTMLAnchorElement>;
  };
};

const aspectRatios: Record<
  CardGenericProps["variant"],
  ImageProps["aspectRatio"]
> = {
  "primary-large": "9:11",
  "primary-medium": "3:4",
  "primary-small": "1:1",
  secondary: "16:9",
  "secondary-image-full": "1:1",
};

const cardVariants = cva("relative flex flex-col overflow-hidden rounded-md", {
  variants: {
    variant: {
      "primary-large": "h-full w-full",
      "primary-medium": "h-full w-full",
      "primary-small": "h-full w-full",
      secondary:
        "min-h-full w-full gap-2 border border-border bg-background p-5 shadow",
      "secondary-image-full": "min-h-full w-full gap-2 bg-background",
    },
  },
});

const imageVariants = cva("relative w-full overflow-hidden", {
  variants: {
    variant: {
      "primary-large": "h-full w-full",
      "primary-medium": "h-full",
      secondary: "mb-2 rounded-md",
      "primary-small": "h-full",
      "secondary-image-full": "w-full rounded-md",
    },
  },
});

const contentVariants = cva("", {
  variants: {
    variant: {
      "primary-large":
        "absolute inset-0 flex flex-col justify-end bg-[linear-gradient(180deg,rgba(0,0,0,0)_50%,rgba(0,0,0,0.9)_100%)] p-5",
      "primary-medium":
        "absolute inset-0 flex flex-col justify-end bg-[linear-gradient(180deg,rgba(0,0,0,0)_50%,rgba(0,0,0,0.9)_100%)] p-5",
      secondary: "flex flex-1 flex-col justify-between",
      "primary-small":
        "absolute inset-0 flex flex-col justify-end bg-[linear-gradient(180deg,rgba(0,0,0,0)_50%,rgba(0,0,0,0.9)_100%)] p-5",
      "secondary-image-full": "flex flex-1 flex-col justify-between",
    },
  },
});

export function CardGeneric(props: Readonly<CardGenericProps>) {
  const variant = props.variant;

  if (variant === "secondary" || variant === "secondary-image-full") {
    return (
      <div className={cn(cardVariants({ variant }))}>
        <div className={cn(imageVariants({ variant }))}>
          {props.overlayImage && (
            <Image
              alt={props.overlayImage.alt}
              aspectRatio={aspectRatios[variant]}
              fill
              src={props.overlayImage.url}
            />
          )}
        </div>
        <div className={cn(contentVariants({ variant }))}>
          <div className="mt-2 mb-2">
            <Text color="black" variant="h4">
              {props.title}
            </Text>
            {props.description && (
              <div
                className={cn(
                  "mt-2 mb-2",
                  variant === "secondary-image-full" &&
                    "line-clamp-3 overflow-hidden"
                )}
              >
                <Text color="black" variant="body-sm">
                  {props.description}
                </Text>
              </div>
            )}
          </div>
          <div className="mt-2">
            <Button
              href={props.button.href}
              onClick={props.button.onClick}
              variant="default"
            >
              {props.button.text}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cardVariants({ variant })}>
      <div className={imageVariants({ variant })}>
        {props.overlayImage && (
          <Image
            alt={props.overlayImage.alt}
            aria-hidden="true"
            aspectRatio={aspectRatios[variant]}
            className="pointer-events-none absolute inset-0 h-full w-full rounded-md object-cover"
            fill
            src={props.overlayImage.url}
          />
        )}
        <div
          className={cn(
            contentVariants({ variant }),
            variant === "primary-small" && "text-center"
          )}
        >
          <Text color="white" variant="h3">
            {props.title}
          </Text>
          {props.description && (
            <div className="mb-4">
              <Text color="white" variant="body-md">
                {props.description}
              </Text>
            </div>
          )}
          <div
            className={cn(
              variant === "primary-small" && "mt-2 flex justify-center"
            )}
          >
            <Button
              className={cn(
                variant === "primary-large" &&
                  "rounded-md bg-muted text-foreground shadow-sm",
                variant === "primary-small" && "px-3"
              )}
              href={props.button.href}
              onClick={props.button.onClick}
              variant="outline"
            >
              {props.button.text}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
